// piano-frontend/src/utils/pwa.js
// 홈 화면(PWA) 설치 유도용 유틸.
// beforeinstallprompt(안드로이드/데스크톱 크롬)를 모듈 로드 시점에 한 번 잡아두고,
// 원하는 순간(예: 빈자리 대기 신청 직후)에 네이티브 설치 프롬프트를 띄울 수 있게 한다.
// iOS Safari는 beforeinstallprompt가 없어 수동 안내(공유 → 홈 화면에 추가)만 가능하다.
//
// iOS는 "설치됨"을 감지할 API가 없어, 홈 화면 아이콘으로 한 번이라도 실행되면(=standalone)
// localStorage에 설치 플래그를 남겨, 이후 Safari 탭으로 접속해도 설치 유도를 반복하지 않는다.

const INSTALLED_KEY = 'pwa-installed';
const NUDGE_KEY = 'waitlist-install-nudge-last';
const NUDGE_INTERVAL = 14 * 24 * 60 * 60 * 1000; // 14일

let deferredInstallPrompt = null;
let installed = false;

// 홈 화면에 설치되어 실행 중인지 (standalone). iOS는 navigator.standalone.
export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
}

export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function readInstalledFlag() {
  try {
    return localStorage.getItem(INSTALLED_KEY) === 'true';
  } catch {
    return false;
  }
}

function markInstalled() {
  installed = true;
  try {
    localStorage.setItem(INSTALLED_KEY, 'true');
  } catch {
    /* noop */
  }
}

// 설치된 것으로 볼 수 있는가?
// - 지금 standalone 실행 중이거나
// - 과거 standalone 실행/ appinstalled 로 남긴 설치 플래그가 있으면 true
export function isProbablyInstalled() {
  return installed || isStandalone() || readInstalledFlag();
}

// beforeinstallprompt / appinstalled 캡처 + standalone 실행 시 설치 플래그 영구 기록
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // 브라우저 기본 미니 인포바를 막고 우리가 원하는 시점에 띄운다.
    e.preventDefault();
    deferredInstallPrompt = e;
  });
  window.addEventListener('appinstalled', () => {
    markInstalled();
    deferredInstallPrompt = null;
  });
  // 홈 화면 아이콘으로 실행된 적이 있으면(=지금 standalone) 설치 사실을 남긴다.
  if (isStandalone()) markInstalled();
}

// 지금 설치 유도가 의미있는가? (미설치 & 유도 수단이 있음)
// - 이미 설치된 것으로 판단되면 false
// - iOS: 수동 안내 가능 → true
// - 안드로이드/데스크톱: 네이티브 프롬프트를 잡아둔 경우에만 true
export function canInstall() {
  if (isProbablyInstalled()) return false;
  if (isIOS()) return true;
  return !!deferredInstallPrompt;
}

// 안드로이드/데스크톱 네이티브 설치 프롬프트 실행. outcome('accepted'|'dismissed') 또는 null 반환.
export async function promptNativeInstall() {
  if (!deferredInstallPrompt) return null;
  const evt = deferredInstallPrompt;
  deferredInstallPrompt = null; // 프롬프트는 1회용
  try {
    evt.prompt();
    const choice = await evt.userChoice;
    if (choice?.outcome === 'accepted') markInstalled();
    return choice?.outcome ?? null;
  } catch {
    return null;
  }
}

// 설치 유도 스누즈 (거슬리지 않게 재노출 간격 제한)
export function installNudgeSnoozed() {
  try {
    const last = localStorage.getItem(NUDGE_KEY);
    return !!last && (Date.now() - parseInt(last, 10)) < NUDGE_INTERVAL;
  } catch {
    return false;
  }
}

export function markInstallNudged() {
  try {
    localStorage.setItem(NUDGE_KEY, String(Date.now()));
  } catch {
    /* noop */
  }
}
